import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import DashboardGroupTop from './DashboardGroupTop';
import DashboardTable from '../DashboardTable';
import useRequest from '../../../hooks/useRequest';
import AddGroupPopup from './AddGroupPopup';
import EditGroupPopup from './EditGroupPopup';
import { useParams } from 'react-router-dom';

function DashboardGroup() {
  const { id: brokerId } = useParams();
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [error, setError] = useState(null);
  const { sendRequest } = useRequest();

  const fetchGroups = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      let url = '/api/group';
      const params = new URLSearchParams();
      
      if (brokerId) params.append('broker_id', brokerId);
      if (searchQuery) params.append('search', searchQuery);
      if (selectedStatus) {
        const statusValue = selectedStatus === 'Active' ? true : false;
        params.append('is_active', statusValue);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await sendRequest('GET', url);
      setGroups(response || []);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      setError(error.message || 'Failed to fetch groups');
    } finally {
      setIsLoading(false);
    }
  }, [sendRequest, brokerId, searchQuery, selectedStatus]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleDelete = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) {
      return;
    }

    try {
      await sendRequest('DELETE', `/api/group/?id=${groupId}`);
      fetchGroups();
    } catch (error) {
      console.error('Failed to delete group:', error);
      setError(error.message || 'Failed to delete group');
    }
  };

  const handleEdit = (group) => {
    setSelectedGroup(group);
    setIsEditPopupOpen(true);
  };

  const handleAddGroup = () => {
    setIsPopupOpen(true);
  };

  const handleGroupCreated = () => {
    fetchGroups();
  };

  const handleGroupUpdated = () => {
    fetchGroups();
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const handleStatusClear = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setSelectedStatus(null);
      setIsTransitioning(false);
    }, 300);
  };

  const handleSearch = (value) => {
    setSearchQuery(value);
  };

  const columnNames = ['ID', 'Facebook ID', 'Last Post ID', 'Status', 'Actions'];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <motion.div 
      className="p-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div
        variants={itemVariants}
        className="mb-6"
      >
        <DashboardGroupTop 
          totalGroups={groups.length}
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          onStatusClear={handleStatusClear}
          onSearch={handleSearch}
          onAddGroup={handleAddGroup}
        />
      </motion.div>

      <motion.div
        variants={itemVariants}
        className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
      >
        <AnimatePresence mode="wait">
          {error ? (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex flex-col items-center justify-center p-8 text-center"
            >
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl text-red-600">⚠️</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Groups</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={fetchGroups}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </motion.div>
          ) : isLoading || isTransitioning ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className="flex items-center justify-center p-8"
            >
              <div className="relative">
                <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm text-blue-600 font-medium">Loading...</span>
                </div>
              </div>
            </motion.div>
          ) : groups.length === 0 ? (
            <motion.div
              key="empty"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex flex-col items-center justify-center p-8 text-center"
            >
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                <span className="text-2xl text-blue-600">😔</span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Groups Found</h3>
              <p className="text-gray-500">
                {searchQuery 
                  ? "No groups match your search criteria"
                  : "There are no groups in the system yet"}
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <DashboardTable 
                columnNames={columnNames} 
                data={groups.map(group => ({
                  id: group.id,
                  fb_id: group.fb_id || '-',
                  last_post_id: group.last_post_id || '-',
                  status: (
                    <div className="flex items-center space-x-2">
                      <span className={`inline-block w-2 h-2 rounded-full ${
                        group.is_active ? 'bg-green-500' : 'bg-red-500'
                      }`}></span>
                      <span>{group.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                  ),
                  actions: (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(group)}
                        className="p-2 text-gray-600 hover:text-gray-700 transition-colors"
                        title="Edit"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(group.id)}
                        className="p-2 text-red-600 hover:text-red-700 transition-colors"
                        title="Delete"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )
                }))}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AddGroupPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        onSuccess={handleGroupCreated}
        brokerId={brokerId}
      />

      <EditGroupPopup
        isOpen={isEditPopupOpen}
        onClose={() => setIsEditPopupOpen(false)}
        onSuccess={handleGroupUpdated}
        group={selectedGroup}
      />
    </motion.div>
  );
}

export default DashboardGroup; 